/** The People register's words: the List, its tools, the bulk send, Add teacher and the managed accounts. */
export const registerCopy = {
  eyebrow: "Registrar",
  title: "People",
  description: "Add people, manage their roles and access, and keep records in one place.",

  registerLabel: "People",
  registerJob: "Find and manage the institution's people.",
  registerUnavailable: "Couldn't read the register",
  registerUnavailableDetail: "No row, count or access state is inferred from the failed read.",
  noMatch: "No people match this view",
  noMatchSub: "Change or clear a filter to see more people.",
  noPeople: "No people are registered yet",
  noPeopleSub: "Add a person to get started.",
  find: "Search name, SID or profile",
  allShown: "All shown",
  matchingPerson: "matching person",
  matchingPeople: "matching people",
  allTerms: "Teaching scope: all terms",
  openPerson: "Open person",
  draftKept: "Draft kept in this browser",
  noName: "No name yet",
  noLmsAccount: "No LMS sign-in",
  accessActive: "LMS sign-in active",
  accessWithdrawn: "LMS sign-in suspended",
  accountRetired: "LMS sign-in suspended",

  view: "View",
  viewOperational: "People",
  viewAll: "Everyone",
  viewArchived: "Archived people",
  viewAttention: "Google Workspace work",
  viewRetired: "Retired accounts",
  viewCurrent: "Current students",
  viewAlumni: "Alumni",
  viewHistory: "Student history",
  access: "Access",
  accessAll: "Any access",
  accessFilterActive: "LMS sign-in active",
  accessFilterDeactivated: "LMS sign-in suspended",
  accessFilterNone: "No LMS sign-in",
  relationship: "Relationship",
  relationshipAll: "Any relationship",
  relationshipStudent: "Student",
  relationshipFaculty: "Faculty",
  relationshipAdmin: "Admin",
  relationshipStaff: "Staff without profile",
  attention: "Incomplete records",
  attentionAll: "Any record",
  attentionUnnamed: "No name yet",
  attentionUnlinked: "Unlinked student",
  attentionProfile: "Teacher without sign-in",
  program: "Program",
  programAll: "All programs",
  cohort: "Cohort",
  cohortAll: "All cohorts",
  status: "Student status",
  statusAll: "Any student status",
  teaching: "Teaching",
  teachingAll: "Any teaching",
  teachingAssigned: "Assigned",
  teachingUnassigned: "Unassigned",
  term: "Term",
  termAll: "All terms",
  optionsUnavailable: "Couldn't read the choices",
  page: "Showing",

  bulkLabel: "Send fresh setup emails",
  bulkJob: "Send a fresh setup email on each selected person's invitation.",
  bulkNothing: "Select people to send setup emails",
  bulkNothingDetail: "Choose people in the register. Only those with a current invitation are sent one.",
  included: "People included",
  leftOutPart: "Left out",
  // Why a selected person is left out: one who signs in is never sent a setup email.
  leftOutSignsIn: "Already signs in",
  leftOutNoInvitation: "No current invitation",
  nobodyIncluded: "Nobody selected has a current invitation.",
  clearSelection: "Clear selection",
  readyToSend: "Ready to send",
  nobodyToSend: "Nobody to send to",
  sent: "Sent",
  partlySent: "Partly sent",
  notSent: "Not sent",
  bulkCap: "When you send",
  bulkNote: "Nobody is matched by email or name. The selection stays until you send or clear it.",
  bulkRefused: "Not sent.",
  bulkUnknown: "Sending setup emails",
  bulkUnknownSentence: "We could not confirm whether the emails were sent. Check before sending again.",
  bulkCheckNote: "Reads the invitations. Nothing is sent again.",
  retryNote: "Resends only the ones that failed.",
  otherEmailsSent: "The other emails were sent earlier",
  checkInvitation: "Not confirmed. Open this invitation and check it before sending again.",

  addLabel: "Add person",
  addJob: "Add a person, then manage their records and sign-in here.",
  addDetail: "Create a person or teaching record. Invite them to sign in from their record.",
  everyTeacherSignsIn: "Every teacher can sign in",
  addOpenDetail: "Nothing is created until there is a name.",
  who: "Who",
  name: "Name",
  named: "Named",
  nameNeeded: "Name needed",
  readyToAdd: "Ready to add",
  aTeacherWithoutSignIn: "A teacher without a sign-in.",
  enterName: "Enter the teacher's name.",
  addCap: "When you add",
  addEffect: "Create the person, without changing sign-in access or sending an email",
  addShows: "Open brings their record here, in place of the register",
  added: "Added",
  addRefused: "Not added.",
  addUnknown: "Adding the person",
  addUnknownSentence: "We could not confirm whether the person was added. Check before adding again.",
  addCheckNote: "Reads the register. Nothing is added again.",

  managedLabel: "Institution-managed Google accounts",
  managedJob: "The Google Workspace accounts the institution manages, counted for the whole institution.",
  managedUnavailable: "Couldn't read the managed accounts",
  managedDetail: "Google Workspace owns these accounts. Counts for the whole institution, not this page of the register.",
  managed: "Managed",
  suspended: "Suspended, still using a license",
  notConverged: "Waiting on Google Workspace",
  unresolved: "Need manual recovery",
} as const;

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export const registerText = {
  // The total is the head's figure; the state says only how many this page shows.
  shown: (shown: number) => `${shown} shown`,
  scope: (term: string) => `Teaching scope: ${term}`,
  pageOption: (from: number, to: number) => `People ${from} to ${to}`,
  selected: (count: number, hidden: number) => (hidden ? `${count} selected, ${hidden} not shown by this filter` : `${count} selected`),
  withAndWithout: (withInvitation: number, without: number) => `${withInvitation} with a current invitation · ${without} without one`,
  review: (count: number) => `Review ${count} selected`,
  selectPerson: (name: string) => `Select ${name}`,
  noneSelectedHaveOne: (count: number) => `None of the ${count} selected has a current invitation.`,
  // With nobody included yet the verb names the act, never "Send 0".
  send: (count: number) => (count ? `Send ${plural(count, "setup email", "setup emails")}` : "Send setup emails"),
  sendTo: (name: string) => `Send a fresh setup email to ${name}, to the address on their invitation`,
  sentTo: (name: string) => `Sent a fresh setup email to ${name}`,
  leaveOut: (name: string, reason: string) => `Leave out ${name}: ${reason.charAt(0).toLowerCase()}${reason.slice(1)}`,
  sentCount: (count: number) => `Sent ${plural(count, "setup email", "setup emails")}`,
  sentOf: (sent: number, of: number) => `Sent ${sent} of ${of}`,
  retry: (count: number) => `Retry ${plural(count, "failed email", "failed emails")}`,
  unsigned: (count: number) => `${count} ${count === 1 ? "teacher has" : "teachers have"} no sign-in`,
  addedName: (name: string) => `Added ${name}`,
  open: (name: string) => `Open ${name.trim().split(/\s+/)[0] ?? name}`,
};
